// ============================================================================
// FNE-BRIDGE Frontend - Client HTTP Axios
// Intercepteurs : JWT auto-attach, refresh token, error handling
// ============================================================================

import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from '../config/env';
import { STORAGE_KEYS } from '../config/constants';

// ============================================================================
// Instance Axios
// ============================================================================

const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeout,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// ============================================================================
// Helpers
// ============================================================================

function getStoredTokens(): { access_token: string; refresh_token: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.AUTH);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.tokens || null;
  } catch {
    return null;
  }
}

function clearAuth(): void {
  localStorage.removeItem(STORAGE_KEYS.AUTH);
}

// ============================================================================
// Request interceptor : attach JWT
// ============================================================================

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const tokens = getStoredTokens();
    if (tokens?.access_token) {
      config.headers.Authorization = `Bearer ${tokens.access_token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================================
// Response interceptor : refresh token on 401
// ============================================================================

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Si 401 et pas déjà en retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Ne PAS tenter un refresh pour les routes d'auth (login, OTP, etc.)
      // Un 401 sur login = identifiants incorrects, pas un token expiré
      const authRoutes = ['/auth/login', '/auth/validate-otp', '/auth/reset-password', '/auth/create-account'];
      const isAuthRoute = authRoutes.some((r) => originalRequest.url?.includes(r));
      if (isAuthRoute) {
        return Promise.reject(error);
      }

      // Si c'est la route de refresh elle-même, logout
      if (originalRequest.url?.includes('/auth/refresh-token')) {
        clearAuth();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Mettre en file d'attente
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const tokens = getStoredTokens();
        if (!tokens?.refresh_token) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${env.apiBaseUrl}/auth/refresh-token`, {
          refresh_token: tokens.refresh_token,
        });

        const newTokens = response.data.data;

        // Mettre à jour le store
        const raw = localStorage.getItem(STORAGE_KEYS.AUTH);
        if (raw) {
          const parsed = JSON.parse(raw);
          parsed.state.tokens = newTokens;
          localStorage.setItem(STORAGE_KEYS.AUTH, JSON.stringify(parsed));
        }

        processQueue(null, newTokens.access_token);

        originalRequest.headers.Authorization = `Bearer ${newTokens.access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Detect license-expired 403 responses and set the flag
    if (error.response?.status === 403) {
      const msg = (error.response?.data as any)?.error?.message || (error.response?.data as any)?.message || '';
      if (typeof msg === 'string' && msg.toLowerCase().includes('licence expir')) {
        try {
          const raw = localStorage.getItem(STORAGE_KEYS.AUTH);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.state) {
              parsed.state.licenseExpired = true;
              localStorage.setItem(STORAGE_KEYS.AUTH, JSON.stringify(parsed));
            }
          }
        } catch { /* silent */ }
        // Redirect to settings if not already there
        if (!window.location.pathname.startsWith('/settings')) {
          window.location.href = '/settings';
        }
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// Proactive token refresh — refresh BEFORE the access token expires
// Runs every 50 minutes (access token lives 1 hour) and on tab re-focus.
// ============================================================================

const REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes
let refreshIntervalId: ReturnType<typeof setInterval> | null = null;

async function proactiveRefresh(): Promise<void> {
  const tokens = getStoredTokens();
  if (!tokens?.refresh_token) return;

  try {
    const response = await axios.post(`${env.apiBaseUrl}/auth/refresh-token`, {
      refresh_token: tokens.refresh_token,
    });
    const newTokens = response.data.data;
    const raw = localStorage.getItem(STORAGE_KEYS.AUTH);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.state) {
        parsed.state.tokens = newTokens;
        localStorage.setItem(STORAGE_KEYS.AUTH, JSON.stringify(parsed));
      }
    }
  } catch {
    // Silent fail — the 401 interceptor will handle it if the token is truly dead
  }
}

function startProactiveRefresh(): void {
  if (refreshIntervalId) return;
  refreshIntervalId = setInterval(proactiveRefresh, REFRESH_INTERVAL_MS);
}

function stopProactiveRefresh(): void {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
}

// Start/stop based on auth state
function checkAndStartRefresh(): void {
  const tokens = getStoredTokens();
  if (tokens?.refresh_token) {
    startProactiveRefresh();
  } else {
    stopProactiveRefresh();
  }
}

// Refresh when tab becomes visible again (user returns after being away)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const tokens = getStoredTokens();
      if (tokens?.refresh_token) {
        proactiveRefresh();
      }
    }
  });

  // Also listen for storage changes (login/logout in another tab)
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEYS.AUTH) {
      checkAndStartRefresh();
    }
  });

  // Start on load if already authenticated
  checkAndStartRefresh();
}

export default apiClient;
